import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronUp, ListChecks, X } from "lucide-react";
import type { JobRow } from "@gwrctl/api-client";
import { getJob } from "@gwrctl/shared";
import { jobTitle } from "../lib/labels.ts";
import { AppIcon } from "./AppIcon.tsx";
import { ProgressBar, StatusPill } from "./Ui.tsx";

/**
 * Her sayfada sağ altta duran iş kutusu: kuyrukta / çalışan işler ve son biten
 * işlerin sonucu. Uygulama sayfasındaki RunPanel yalnızca o sayfada ve tek iş için;
 * iki iş başlatıp sayfa değiştirince hangisinin ne durumda olduğu kayboluyordu.
 *
 * Biten işler RECENT_MS boyunca (ya da kullanıcı kapatana dek) görünür kalır.
 * Sağlık gözcüsünün periyodik işleri gösterilmez — gürültü.
 */

const ACTIVE = new Set(["queued", "claimed", "running"]);
const RECENT_MS = 30 * 60_000;
const DISMISSED_KEY = "gwrctl.dock.dismissed";
const COLLAPSED_KEY = "gwrctl.dock.collapsed";

function readDismissed(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(DISMISSED_KEY) ?? "[]") as string[]);
  } catch {
    return new Set();
  }
}

function writeDismissed(ids: Set<string>): void {
  try {
    // Yalnızca son kayıtlar tutulur; liste sonsuza büyümesin.
    localStorage.setItem(DISMISSED_KEY, JSON.stringify([...ids].slice(-100)));
  } catch {
    /* depolama yoksa kapatma yalnızca bu oturumda geçerli */
  }
}

export function JobDock({
  jobs,
  apps,
}: {
  jobs: JobRow[] | undefined;
  apps: { id: string; displayName: string }[] | undefined;
}): ReactNode {
  const [dismissed, setDismissed] = useState(readDismissed);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSED_KEY) === "1";
    } catch {
      return false;
    }
  });
  // Dakikada bir yeniden çiz: RECENT_MS dolan işler kendiliğinden düşsün.
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);

  const items = useMemo(() => {
    const list = (jobs ?? []).filter((j) => {
      if (j.createdBy === "watchdog") return false;
      if (ACTIVE.has(j.status)) return true;
      if (dismissed.has(j.id)) return false;
      const finished = j.finishedAt ? new Date(j.finishedAt).getTime() : 0;
      return now - finished < RECENT_MS;
    });
    // Aktifler üstte, kendi içinde oluşturulma sırasıyla (kuyruk sırası görünsün).
    return list.sort((a, b) => {
      const aa = ACTIVE.has(a.status) ? 0 : 1;
      const bb = ACTIVE.has(b.status) ? 0 : 1;
      if (aa !== bb) return aa - bb;
      return aa === 0
        ? a.createdAt.localeCompare(b.createdAt)
        : (b.finishedAt ?? "").localeCompare(a.finishedAt ?? "");
    });
  }, [jobs, dismissed, now]);

  const running = items.filter((j) => j.status === "running" || j.status === "claimed").length;
  const queued = items.filter((j) => j.status === "queued").length;
  const failed = items.filter((j) => j.status === "failed").length;
  const finished = items.filter((j) => !ACTIVE.has(j.status));

  // Yeni bir iş eklenince kutu kendiliğinden açılsın (kapalıyken görünmeden başlamasın).
  const activeIds = items.filter((j) => ACTIVE.has(j.status)).map((j) => j.id).join(",");
  const seenActive = useRef<string>("");
  useEffect(() => {
    const prev = new Set(seenActive.current.split(",").filter(Boolean));
    if (activeIds.split(",").some((id) => id && !prev.has(id)) && seenActive.current !== "") {
      setCollapsed(false);
    }
    seenActive.current = activeIds || " ";
  }, [activeIds]);

  function toggle(): void {
    setCollapsed((c) => {
      try {
        localStorage.setItem(COLLAPSED_KEY, c ? "0" : "1");
      } catch {
        /* yoksay */
      }
      return !c;
    });
  }

  function dismiss(ids: string[]): void {
    setDismissed((prev) => {
      const next = new Set(prev);
      for (const id of ids) next.add(id);
      writeDismissed(next);
      return next;
    });
  }

  if (items.length === 0) return null;

  const appName = (id: string | null) => apps?.find((a) => a.id === id)?.displayName;

  const summary = [
    running ? `${running} çalışıyor` : null,
    queued ? `${queued} sırada` : null,
    failed ? `${failed} başarısız` : null,
    !running && !queued && !failed ? `${finished.length} bitti` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <section
      aria-label="İş durumu"
      className="fixed right-3 bottom-3 left-3 z-30 overflow-hidden rounded-[10px] border border-neutral-800 shadow-2xl shadow-black/50 sm:left-auto sm:w-[360px]"
      style={{ background: "#151722" }}
    >
      <button
        type="button"
        onClick={toggle}
        aria-expanded={!collapsed}
        className="flex w-full cursor-pointer items-center gap-2 px-3.5 py-2.5 text-left text-sm hover:bg-white/[0.03]"
      >
        <ListChecks size={15} className={running ? "pulse text-accent-300" : "text-neutral-500"} aria-hidden />
        <span className="font-medium">İşler</span>
        <span className={`truncate text-xs ${failed ? "text-danger" : "text-neutral-400"}`}>{summary}</span>
        <span className="ml-auto text-neutral-500">
          {collapsed ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
        </span>
      </button>

      {!collapsed && (
        <>
          <ul className="max-h-[50vh] overflow-y-auto border-t border-neutral-900">
            {items.map((job) => (
              <DockRow
                key={job.id}
                job={job}
                app={appName(job.appId)}
                waitingFor={waitingFor(job, items)}
                onDismiss={ACTIVE.has(job.status) ? undefined : () => dismiss([job.id])}
              />
            ))}
          </ul>
          {finished.length > 0 && (
            <div className="flex border-t border-neutral-900 px-3.5 py-1.5">
              <button
                type="button"
                onClick={() => dismiss(finished.map((j) => j.id))}
                className="ml-auto cursor-pointer rounded-[6px] px-1.5 py-0.5 text-xs text-neutral-500 hover:bg-white/5 hover:text-neutral-200"
              >
                Bitenleri temizle
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

/**
 * Kuyruktaki bir iş neden bekliyor? Aynı uygulamada klasöre yazan başka bir iş
 * sürüyorsa (ya da önünde sıradaysa) sunucu onu bilerek bekletiyor.
 */
function waitingFor(job: JobRow, all: JobRow[]): JobRow | undefined {
  if (job.status !== "queued" || !job.appId || getJob(job.type)?.lock !== "workspace") return undefined;
  return all.find(
    (o) =>
      o.id !== job.id &&
      o.appId === job.appId &&
      ACTIVE.has(o.status) &&
      getJob(o.type)?.lock === "workspace" &&
      (o.status !== "queued" || o.createdAt < job.createdAt),
  );
}

function DockRow({
  job,
  app,
  waitingFor,
  onDismiss,
}: {
  job: JobRow;
  app: string | undefined;
  waitingFor: JobRow | undefined;
  onDismiss: (() => void) | undefined;
}): ReactNode {
  const active = ACTIVE.has(job.status);
  const detail =
    job.status === "queued"
      ? waitingFor
        ? `Sırada — ${jobTitle(waitingFor.type)} bitince başlayacak`
        : "Agent'ın alması bekleniyor"
      : active
        ? (job.step ?? "başlatılıyor…")
        : job.status === "failed"
          ? (job.error ?? "Başarısız oldu")
          : job.status === "succeeded"
            ? "Tamamlandı"
            : null;

  return (
    <li className="flex border-b border-neutral-900 last:border-b-0">
      <Link
        to={`/jobs/${job.id}`}
        className={`flex min-w-0 flex-1 gap-2.5 py-2.5 pl-3.5 hover:bg-white/[0.03] ${onDismiss ? "pr-1" : "pr-3.5"}`}
      >
        {job.appId ? (
          <AppIcon id={job.appId} name={app ?? job.appId} size={20} className="mt-0.5 shrink-0" />
        ) : (
          <span className="mt-0.5 size-5 shrink-0" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="min-w-0 flex-1 truncate text-sm text-neutral-200">
              {app ? <span className="text-neutral-500">{app} · </span> : null}
              {jobTitle(job.type)}
            </span>
            <StatusPill status={job.status} />
          </div>
          {detail && (
            <div
              className={`mt-0.5 line-clamp-2 text-xs break-words ${
                job.status === "failed" ? "text-danger/90" : "text-neutral-500"
              }`}
            >
              {detail}
            </div>
          )}
          {(job.status === "running" || job.status === "claimed") && (
            <div className="mt-1.5">
              <ProgressBar pct={job.pct} />
            </div>
          )}
        </div>
      </Link>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Listeden kaldır"
          title="Listeden kaldır"
          className="mt-2 mr-1.5 grid size-6 shrink-0 cursor-pointer place-items-center rounded-[6px] text-neutral-600 hover:bg-white/10 hover:text-neutral-200"
        >
          <X size={13} />
        </button>
      )}
    </li>
  );
}
